import { Box } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useAuth } from '@verantos-dev/core-auth';
import {
  type AnalyticalVariableListItem,
  DeleteAnalyticalVariableDialog,
  RenameAnalyticalVariableDialog,
} from '@verantos-dev/feature-analytical-variables';
import { ActionMenu, type ActionMenuItemProps } from '@verantos-dev/shared-components';

interface AvLibraryRowActionsProps {
  analyticalVariable: AnalyticalVariableListItem;
}

/**
 * Per-row action menu for the variable library page. Creator-only, mirroring
 * ``AnalyticalDatasetRowActions``'s shape (self-contained menu + own dialog
 * state).
 *
 * - **Rename** (OR-6090) — no flag; the creator always sees it.
 * - **Delete** (OR-6234) behind ``research_assistant_variable_delete``. A saved
 *   variable with an edit open looks like any other row here; the dialog's
 *   usage read is where that fact surfaces.
 *   TODO: cleanup with research_assistant_variable_delete ff
 */
export const AvLibraryRowActions = ({ analyticalVariable }: AvLibraryRowActionsProps) => {
  const intl = useIntl();
  const {
    user: { userId: currentUserId },
  } = useAuth();
  const isDeleteEnabled = useFeatureFlagSplit(
    'research_assistant_variable_delete',
    false
  ) as boolean;
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const creatorUserId = analyticalVariable.createdBy?.userId ?? null;
  const isCreator = creatorUserId !== null && creatorUserId === currentUserId;

  const showRename = isCreator;
  const showDelete = isCreator && isDeleteEnabled;

  if (!showRename && !showDelete) return null;

  const displayName = analyticalVariable.dataElement || analyticalVariable.name;
  const id = analyticalVariable.analyticalVariableId;
  const menu: ActionMenuItemProps[] = [];
  if (showRename) {
    menu.push({
      id: `avLibraryActionMenu-rename-${id}`,
      label: intl.formatMessage({ id: 'projects.analyticalVariables.actionMenu.rename.label' }),
      onClick: () => setIsRenameOpen(true),
      icon: 'rename',
    });
  }
  if (showDelete) {
    menu.push({
      id: `avLibraryActionMenu-delete-${id}`,
      label: intl.formatMessage({ id: 'projects.analyticalVariables.actionMenu.delete.label' }),
      onClick: () => setIsDeleteOpen(true),
      icon: 'delete',
    });
  }

  return (
    <Box data-testid={`av-library-row-actions-${id}`} sx={{ display: 'inline-flex' }}>
      <ActionMenu id={`avLibraryActionMenu-${id}`} menuItems={menu} stopClickEventPropagation />
      {isRenameOpen && (
        <RenameAnalyticalVariableDialog
          analyticalVariableId={id}
          currentName={displayName}
          onClose={() => setIsRenameOpen(false)}
        />
      )}
      {isDeleteOpen && (
        <DeleteAnalyticalVariableDialog
          analyticalVariableId={id}
          variableName={displayName}
          onClose={() => setIsDeleteOpen(false)}
        />
      )}
    </Box>
  );
};
